/* The application bar, transcribed from `Builder planu - tydzien - programator.dc.html`'s first row.
 *
 * The mock's own declaration, value for value:
 *
 *   flex:none; display:flex; align-items:center; gap:12px; height:56px; padding:0 20px;
 *   background:var(--bg-card); border-bottom:1px solid var(--border)
 *
 * ── `--text-3` IS used for text here ─────────────────────────────────────────────────────────
 * Every other builder stylesheet in this slice used to open with a note explaining that KANON's
 * tertiary ink fails AA and that the hierarchy would be carried by size and weight instead. The
 * owner looked at the result beside the mock on 8 September 2026 and rejected it: the mock's
 * colours are the colours. The crumb is `--text-3` because the mock writes `--text-3`.
 *
 * ── What this bar no longer draws, and where each thing went ─────────────────────────────────
 * The mock's bar is six elements. Ours carried eleven, and the five that are gone are gone from
 * SIGHT only — every capability behind them survives:
 *
 * · **The plan-cover thumbnail** (`PlanMetaPopover`) is not mounted here any more.
 * · **The dashed rename underline** is gone; renaming is the title's CONTEXT MENU — right-click,
 *   or the keyboard menu key while the title has focus. The title is a `<button>` drawn to be
 *   byte-identical to the mock's `<span>`, which is what gives the keyboard path an element to
 *   stand on; it has no border, no background, no underline and no pointer cursor.
 * · **Undo and redo** are the chords alone (`usePlanShortcuts`, wired at the screen). The mock
 *   draws no history pair and the owner's instruction is that nothing outside the mock is visible.
 * · **The word "Zapisz"** and **the blocked-save sentence** are gone. The 34×34 floppy is the only
 *   save; it dispatches exactly what the text button dispatched, and its refusal travels in
 *   `aria-describedby` and on `title`.
 * · **The lock chip** ("Plan przypisany · tyg. 1–4 zrealizowane") is visually hidden rather than
 *   removed — it is information, not an affordance, and dropping it from the accessibility tree
 *   would lose a fact the trainer has no other way to read on this screen.
 *
 * The chip that REMAINS is the provenance one, because the mock draws it: "Szablon". */

.bar {
  display: flex;
  flex: none;
  gap: var(--space-3);
  align-items: center;
  height: var(--h-app-bar);
  padding: var(--space-0) var(--space-20px);
  background: var(--bg-card);
  border-bottom: var(--border-hairline) solid var(--border);
}

/* `Programy /` — one 12.5px/600 line in `--text-3`, with the slash at half opacity. It is a `nav`
 * holding a link, so the way back is a real link; the mock's own element is a span and the two are
 * drawn identically, since the mock's stylesheet colours `a` in the accent and this must not. */

/* One inline run, not a flex row: the mock writes `Programy <b>/</b>` inside a single span, so what
 * separates the two is an ordinary space character at 12.5px, not a gap. A 4px flex gap measured
 * 0.4px narrower. */
.crumb {
  flex: none;
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
  white-space: nowrap;
}

.crumbLink {
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
  text-decoration: none;
}

.crumbLink:hover {
  color: var(--text-2);
}

.crumbLink:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
  border-radius: var(--radius-4px);
}

/* The mock's `<b style="opacity:.5;font-weight:600">/</b>`. */
.sep {
  font-weight: var(--fw-semibold);
  opacity: 0.5;
}

/* The plan's name: 16px / 800 / -.02em, one line, `--text`.
 *
 * The `h1` is the layout box and the button inside it is the whole of its content, so the heading's
 * accessible name is the plan's name and nothing else. `AppShell` moves focus here after a route
 * change, which is what announces the screen. */
.planName {
  display: flex;
  flex: none;
  min-width: 0;
  margin: var(--space-0);
  font-size: var(--fs-h3);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-title);
  color: var(--text);
  white-space: nowrap;
}

/* The plan's own ⋯, beside the name.
 *
 * It replaced the title's context menu on 17 September 2026 — the owner's note was that the builder
 * offers no way to delete a plan or to end one, and two of the four acts it now carries are
 * irreversible. The mock draws no mark here; a departure from it is recorded in the component's
 * header, with the reason.
 *
 * Quiet by default and never accented: the accent on this bar is spoken for by Zapisz, and this is
 * a way IN to acts rather than an act. 26px so it sits on the chips' own line rather than on the
 * heading's, which is 4px taller — the bar centres its row, so a taller control here would push the
 * name off the mock's baseline.
 *
 * Quiet is `--text-2`, not `--text-3`. The dimmer ink measures 3.01:1 on this bar in Flame, which
 * clears the 3:1 a graphical control owes by a hundredth — and this control is the way in to two
 * irreversible acts, which is not a thing to ship on the line. */
.planMenu {
  display: inline-grid;
  flex: none;
  place-items: center;
  width: 26px;
  height: 26px;
  padding: var(--space-0);
  color: var(--text-2);
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: var(--radius-8px);
}

.planMenu:hover,
.planMenu[data-popup-open] {
  color: var(--text);
  background: var(--bg-chip);
}

.planMenu:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.planMenuGlyph {
  display: block;
  width: 16px;
  height: 16px;
}

/* The rename field, standing in the heading's own box at the heading's own type so the bar does not
 * change height when it opens. */
.planNameInput {
  min-width: 0;
  padding: var(--space-0);
  font-family: var(--font-body);
  font-size: var(--fs-h3);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-title);
  color: var(--text);
  background: transparent;
  border-width: var(--space-0);
  border-bottom-width: var(--border-hairline);
  border-style: solid;
  border-color: var(--border-2);
  outline: none;
}

/* "Szablon" and "Własnym tempem" — h26, `0 10px`, radius 8, `--bg-chip` under a `--border` hairline,
 * 11.5/700/`--text-2`. One class for both: the sequence mock draws the two chips identically and the
 * mode one simply carries a glyph, which is what the 6px gap is for. */
.chip {
  display: inline-flex;
  flex: none;
  gap: var(--space-6px);
  align-items: center;
  height: 26px;
  padding: var(--space-0) var(--space-10px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  white-space: nowrap;
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-8px);
}

/* The mock's 14×14 loop before the word. `display: block` because an inline SVG sits on the text
 * baseline and drops the chip's centre by two pixels. */
.chipGlyph {
  display: block;
  flex: none;
  width: 14px;
  height: 14px;
}

.spacer {
  flex: 1;
}

/* "Przypisz klientowi" — h34, `0 14px`, radius 11, `--bg-card` under a `--border-2` hairline,
 * 12.5/700/`--text-2`, gap 7 to the 14×14 person. */
.assign {
  display: inline-flex;
  flex: none;
  gap: var(--space-7px);
  align-items: center;
  height: 34px;
  padding: var(--space-0) var(--space-14px);
  font-family: var(--font-body);
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  white-space: nowrap;
  cursor: pointer;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-11px);
}

.assign:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.assign:hover:not([aria-disabled="true"]) {
  color: var(--text);
}

/* A refused control keeps its place in the tab order — the reason is the point of it
 * still being there (view ADR §12). The mock draws no refused state, so nothing about the drawing
 * changes but the ink: dimmed, under the ordinary arrow (owner, 30 September 2026 — `not-allowed`
 * read as a broken button). */
.assign[aria-disabled="true"] {
  color: var(--text-3);
  cursor: default;
}

/* „Asystent" open: the panel beside the week is its pressed state (board S's `.btn.on`). */
.assistantOn,
.assistantOn:hover:not([aria-disabled="true"]) {
  color: var(--bg);
  background: var(--text);
  border-color: var(--text);
}

.assistantMark {
  flex: none;
  width: 18px;
  height: 18px;
}

.assignGlyph {
  display: block;
  flex: none;
  width: 14px;
  height: 14px;
}

/* The save button and its floppy moved to `shared/ui/saveButton.module.css` and
 * `shared/ui/SaveGlyph.tsx` on 17 September 2026, on their second consumer — the survey editor's
 * top bar, which the owner asked to draw this exact square. Nothing about the rendering here
 * changed; the treatment and its reasoning are in that file's header.
 *
 * In the accessibility tree, drawn nowhere: the save's refusal, the assign's refusal, the save
 * chord, and the lock sentence the mock's bar has no room for. `clip-path` rather than
 * `display: none`, which would take them out of the tree along with the pixels. */
.reason {
  position: absolute;
  width: var(--space-1px);
  height: var(--space-1px);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
